/**
 * CollectionTile Component
 * Displays a collection card with image, metadata, and action buttons
 * Adapted from Figma design for collection browsing
 */
import { mdiCheck, mdiOpenInNew, mdiStar, mdiThumbUp } from "@mdi/js";
import numeral from "numeral";
import React, { useCallback, useEffect, useState, } from "react";
import Debouncer from "../../../util/Debouncer";
import { isCollectionModPresent } from "../../../util/selectors";
import { delayed } from "../../../util/util";
import { nxmFileSize, nxmMod } from "../../icon-paths";
import { joinClasses } from "../../utils/joinClasses";
import { Button } from "../button/Button";
import { Icon } from "../icon/Icon";
import { Typography } from "../typography/Typography";
const debouncer = new Debouncer((func) => {
    func?.();
    return delayed(5000);
}, 5000, false, true);
const userInfoDebouncer = new Debouncer((func) => {
    func?.();
    return Promise.resolve();
}, 10000, false, true);
const Stat = ({ children, iconPath, }) => (React.createElement("div", { className: "flex items-center gap-x-1" },
    React.createElement(Icon, { className: "text-neutral-subdued", path: iconPath, size: "sm" }),
    React.createElement(Typography, { appearance: "moderate", typographyType: "body-sm" }, children)));
export const CollectionTile = ({ api, collection, isLoggedIn = true, onAddCollection, onViewPage, className, }) => {
    const revision = collection.latestPublishedRevision;
    const [isHovered, setIsHovered] = useState(false);
    const [canBeAdded, setCanBeAdded] = useState(true);
    const [pending, setPending] = useState(false);
    const addCollectionDebounced = () => debouncer.schedule(() => setPending(false), () => onAddCollection?.());
    useEffect(() => {
        const state = api?.getState?.();
        if (!state) {
            // No state available means we're likely in demo mode, so skip checks.
            //  Need a better mock for the api.
            return;
        }
        const collectionModInstalled = isCollectionModPresent(state, collection.slug);
        setCanBeAdded(!collectionModInstalled && isLoggedIn);
    }, [api, collection.slug, pending, isHovered, isLoggedIn]);
    // Refresh user info when user hovers on the tile, debounced to once per 5 seconds
    useEffect(() => {
        if (isHovered && api?.events) {
            userInfoDebouncer.schedule(undefined, () => {
                api.events.emit("refresh-user-info");
            });
        }
    }, [isHovered]);
    const addCollection = useCallback(() => {
        if (!pending && canBeAdded && isLoggedIn) {
            setPending(true);
            addCollectionDebounced();
        }
    }, [onAddCollection, canBeAdded, pending, isLoggedIn]);
    // Find the "Easy install" badge (if it exists)
    const easyInstallBadge = collection.badges?.find((badge) => badge.name.toLowerCase() === "easy install");
    return (React.createElement("div", { className: joinClasses(["w-full rounded-md bg-surface-mid", className]), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false) },
        React.createElement("div", { className: "flex items-start gap-x-3.5 p-3" },
            React.createElement("div", { className: "relative flex aspect-4/5 w-full max-w-35 shrink-0 items-center justify-center overflow-hidden rounded-xs bg-surface-translucent-low" },
                React.createElement("img", { alt: collection.name, className: "absolute z-1 h-full max-w-none", src: collection.tileImage?.thumbnailUrl }),
                easyInstallBadge && (React.createElement("div", { className: "absolute inset-x-0 bottom-0 z-2" },
                    React.createElement(Typography, { appearance: "none", className: "flex h-7 items-center gap-x-1 bg-info-weak px-1.5 text-info-50", typographyType: "title-xs" },
                        React.createElement(Icon, { path: mdiStar, size: "xs" }),
                        React.createElement("span", { title: easyInstallBadge.description }, easyInstallBadge.name))))),
            React.createElement("div", { className: "flex min-w-0 grow flex-col gap-y-1.5" },
                React.createElement("div", { className: "flex flex-col gap-y-1" },
                    React.createElement(Typography, { className: "truncate font-semibold wrap-break-word", typographyType: "body-lg" }, collection.name),
                    React.createElement(Typography, { appearance: "moderate", className: "flex items-center gap-x-1", typographyType: "body-sm" },
                        React.createElement("span", { className: "size-4 overflow-hidden rounded-full bg-surface-translucent-low" }, collection.user?.avatar && (React.createElement("img", { alt: collection.user?.name, className: "size-4", src: collection.user.avatar }))),
                        collection.user?.name)),
                React.createElement("div", { className: "flex items-center gap-x-1.5 border-t border-stroke-weak pt-1.5" },
                    React.createElement(Typography, { appearance: "none", className: "text-info-strong", typographyType: "body-sm" }, collection.category?.name),
                    revision.adultContent && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "size-1 rotate-45 bg-neutral-subdued" }),
                        React.createElement(Typography, { appearance: "none", className: "text-danger-strong", typographyType: "body-sm" }, "Adult")))),
                React.createElement("div", { className: "flex items-center gap-x-5 border-t border-stroke-weak pt-1.5" },
                    React.createElement(Stat, { iconPath: mdiThumbUp }, numeral(collection.endorsements).format("0 a")),
                    React.createElement(Stat, { iconPath: nxmFileSize }, numeral(revision.totalSize).format("0.0 b")),
                    React.createElement(Stat, { iconPath: nxmMod }, numeral(revision.modCount).format("0,0"))),
                React.createElement(Typography, { appearance: "subdued", className: "line-clamp-3 border-t border-stroke-weak pt-1.5 wrap-break-word", typographyType: "body-sm" }, collection.summary))),
        React.createElement("div", { className: "flex shrink-0 items-center gap-x-2 rounded-b bg-surface-translucent-low px-3 py-2" },
            !canBeAdded || pending ? (React.createElement(Button, { buttonType: "tertiary", disabled: true, leftIconPath: !isLoggedIn ? undefined : mdiCheck, size: "xs", title: !isLoggedIn ? "Log in to add collections" : undefined }, !isLoggedIn ? "Log in to add" : "Added")) : (React.createElement(Button, { size: "xs", onClick: addCollection }, "Add collection")),
            React.createElement(Button, { buttonType: "tertiary", leftIconPath: mdiOpenInNew, size: "xs", onClick: onViewPage }, "View page"))));
};
